import type { MouseEvent } from 'react';

import { CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import TagIcon from '@crm/shared/assets/icons/tag.svg?react';
import { cn } from '@crm/shared/lib';
import {
    RadioGroup,
    RadioGroupItem,
    SvgIcon,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@crm/shared/ui';
import { DateHelpers } from '@shared/helpers';
import { Avatar, Tag } from '@shared/ui';

import { TaskStatusSwitcher } from './TaskStatusSwitcher';
import { useTaskItem } from './useTaskItem';

import type { TDealTasksData } from '@crm/entities/deal';

interface TaskItemProps {
    task: TDealTasksData['tasks'][number];
    employees: TDealTasksData['employees'];
    tags: TDealTasksData['tags'];
    classes?: string;
    isCompleting?: boolean;
    isCompletionPending?: boolean;
    isStatusUpdating?: boolean;
    status?: NonNullable<TDealTasksData['tasks'][number]['context']['status']>;
    onComplete?: (task: TDealTasksData['tasks'][number]) => void;
    onSwitchStatus?: (
        task: TDealTasksData['tasks'][number],
        status: NonNullable<TDealTasksData['tasks'][number]['context']['status']>
    ) => void;
    onOpenEdit?: (task: TDealTasksData['tasks'][number]) => void;
}

const TASK_OVERDUE_CLASSNAME = 'text-red-600';

export const TaskItem = ({
    classes,
    employees,
    isCompleting = false,
    isCompletionPending = false,
    isStatusUpdating = false,
    onComplete,
    onOpenEdit,
    onSwitchStatus,
    status,
    tags,
    task,
}: TaskItemProps) => {
    const { t } = useTranslation();
    useTaskItem();
    const taskStatus = status ?? task.context.status ?? 'is_waiting';
    const isTaskReadOnly = task.context.highestRole === 'observer';
    const executor = employees.find((employee) => employee.id === task.involved?.executorId);
    const taskTags = (task.context.tags ?? [])
        .map((tagId) => tags.find((tag) => tag.id === tagId))
        .filter((tag): tag is TDealTasksData['tags'][number] => Boolean(tag));
    const completeTask = (event: MouseEvent) => {
        event.stopPropagation();

        if (isCompleting || task.completed) {
            return;
        }

        onComplete?.(task);
    };
    const switchTaskStatus = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (isStatusUpdating || task.completed || isTaskReadOnly) {
            return;
        }

        onSwitchStatus?.(task, taskStatus === 'in_progress' ? 'is_waiting' : 'in_progress');
    };
    const taskDateEnd = task.dateEnd ? DateHelpers.dateFormatter({ date: task.dateEnd }) : '';
    const isTaskOverdue =
        Boolean(taskDateEnd) &&
        !task.completed &&
        DateHelpers.getDateRangeStatus(
            taskDateEnd,
            DateHelpers.getToday(),
            DateHelpers.getToday()
        ) === 'before';
    const hasTaskDetails = Boolean(executor || taskDateEnd || taskTags.length);

    return (
        <div
            data-task-id={task.uid}
            className={cn(
                'flex w-full max-w-full min-w-0 shrink-0 cursor-pointer flex-col items-stretch justify-center gap-2 overflow-hidden rounded-sm border-l-3 py-1 pr-1 pl-3 transition-all duration-200 ease-out hover:shadow-[0_0_10px_0_rgba(0,0,0,0.10)]',
                isCompleting && 'max-h-0 translate-x-2 scale-[0.98] py-0 opacity-0',
                classes
            )}
            style={{ borderColor: task?.context.color }}
            onClick={() => onOpenEdit?.(task)}
        >
            <div className="flex min-h-5 w-full max-w-full min-w-0 items-center gap-1 overflow-hidden">
                <div
                    className="flex shrink-0 items-center justify-center"
                    onClick={(event) => event.stopPropagation()}
                >
                    {!isTaskReadOnly && (
                        <RadioGroup
                            aria-label={t('crm.dealDetailsContent.tasksBlock.aria.status')}
                            value={
                                isCompletionPending || isCompleting || task.completed
                                    ? 'completed'
                                    : null
                            }
                            className="shrink-0"
                        >
                            <RadioGroupItem
                                value="completed"
                                aria-label={t(
                                    'crm.dealDetailsContent.tasksBlock.aria.markCompleted'
                                )}
                                disabled={isCompleting || task.completed}
                                onClick={completeTask}
                            />
                        </RadioGroup>
                    )}
                </div>
                <p
                    className={cn(
                        'min-w-0 flex-1 truncate text-xs leading-4 font-medium text-smtr-main-black-100',
                        (isCompletionPending || isCompleting || task.completed) && 'line-through'
                    )}
                >
                    {task.title}
                </p>
                {!isTaskReadOnly && (
                    <TaskStatusSwitcher
                        classes="ml-7"
                        disabled={isStatusUpdating || task.completed}
                        status={taskStatus}
                        onClick={switchTaskStatus}
                    />
                )}
            </div>
            {hasTaskDetails && (
                <div className="flex w-full max-w-full min-w-0 gap-4 overflow-hidden">
                    {task.involved?.executorId && executor && (
                        <Tooltip>
                            <TooltipTrigger
                                render={
                                    <div className="flex min-w-0 shrink-0 items-center gap-1">
                                        <Avatar
                                            height={16}
                                            width={16}
                                            avatarUrl={executor.avatarUrl}
                                            classes="smetter-css"
                                        />
                                        <p className="max-w-24 min-w-0 truncate text-[10px] leading-3 font-normal text-smtr-main-tertiary-100">
                                            {executor.name}
                                        </p>
                                    </div>
                                }
                            />
                            <TooltipContent>{executor.name}</TooltipContent>
                        </Tooltip>
                    )}
                    {taskDateEnd && (
                        <div className="flex shrink-0 items-center gap-1.5">
                            <CalendarIcon
                                className={cn(
                                    'size-4 text-smtr-main-tertiary-100',
                                    isTaskOverdue && TASK_OVERDUE_CLASSNAME
                                )}
                            />
                            <p
                                className={cn(
                                    'line-clamp-1 w-full justify-start text-[10px] leading-3 font-normal text-smtr-main-black-100',
                                    isTaskOverdue && TASK_OVERDUE_CLASSNAME
                                )}
                            >
                                {taskDateEnd}
                            </p>
                        </div>
                    )}
                    {!!taskTags.length && (
                        <div className="flex max-w-full min-w-0 flex-1 items-center gap-2 overflow-hidden text-smtr-main-tertiary-100">
                            <SvgIcon icon={TagIcon} size="16" className="shrink-0" />
                            <div className="flex max-w-full min-w-0 gap-2 overflow-hidden">
                                {taskTags.map((tag) => (
                                    <Tag classes="smetter-css" key={tag.id} tagName={tag.title} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
